import { React, useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import { useSearchParams, useNavigate } from "react-router-dom";
import supabase from "../../supabase-client";
import {
  Page,
  Button,
  FormLayout,
  TextField,
  Select,
  DropZone,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
} from "@shopify/polaris";
import { useBranch } from "../../context/BranchContext";
import {
  parseCfdiInvoice,
  extractInvoiceFacts,
  extractVehicleDataAI,
  isPlausibleVin,
} from "../../utility/cfdiParser";
import { alegraRequest } from "../../utility/invoicing";

// Swap the old plate for the new one in the car's Alegra product: the name
// (falls back to the standard name when the old plate isn't in it) and the
// "Placa" custom field. If Alegra rejects the custom-field payload, the name —
// which is what prints on the CFDI — is still updated.
const ALEGRA_PLACA_FIELD_ID = "01KGNGPNYVPQ006AH4DWEHMBKP";
async function renameAlegraItemPlate(itemId, oldPlate, newPlate) {
  const item = await alegraRequest(`items/${itemId}`, "GET");
  const current = item?.name || "";
  const name =
    oldPlate && current.toUpperCase().includes(oldPlate.toUpperCase())
      ? current.replace(new RegExp(oldPlate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i"), newPlate)
      : `Servicio de Renta de Unidad - ${newPlate}`;
  const placaField = (item?.customFields || []).find((f) => f.id === ALEGRA_PLACA_FIELD_ID);
  const placaStale = placaField && placaField.value !== newPlate;
  if (name === current && !placaStale) return;
  if (placaStale) {
    try {
      await alegraRequest(`items/${itemId}`, "PUT", {
        name,
        customFields: [{ id: ALEGRA_PLACA_FIELD_ID, value: newPlate }],
      });
      return;
    } catch {
      /* fall through to a name-only update */
    }
  }
  await alegraRequest(`items/${itemId}`, "PUT", { name });
}

function EditCar() {
  const navigate = useNavigate();
  const { branch, branches } = useBranch();
  const [carDetails, setCarDetails] = useState({
    model: undefined,
    plate: "",
    color: "",
    year: undefined,
    acquisition_price: undefined,
    equipment_cost: undefined,
    vin: "",
    motor_no: undefined,
    odometer: undefined,
    notes: "",
    start_date: "",
    branch_id: branch?.id || 1,
  });
  const [searchParams] = useSearchParams();
  const editingId = searchParams.get("id");
  const [isLoading, setIsLoading] = useState(false);
  // Plate as loaded, to detect a plate change on save (the car's Alegra product
  // name carries the plate and prints on every CFDI).
  const [originalPlate, setOriginalPlate] = useState(null);

  const [models, setModels] = useState([]);

  // CFDI invoice drag-and-drop (create mode only) — XML is read to prefill
  // form fields; nothing is uploaded to storage.
  const [xmlFile, setXmlFile] = useState(null);
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [invoiceWarnings, setInvoiceWarnings] = useState([]);
  const [invoiceError, setInvoiceError] = useState(null);
  const [duplicateVin, setDuplicateVin] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const saveCarDetails = async () => {
    setIsLoading(true);
    if (editingId) {
      // Strip branch_id — it's locked at the DB level (only transfer_car_branch
      // RPC can change it). Sending it would raise the trigger.
      const { branch_id: _ignored, ...editableFields } = carDetails;
      // Un auto puede no tener placa todavía (se asigna al emplacar). Guardamos
      // NULL en vez de cadena vacía para que los filtros/guardas la traten como ausente.
      editableFields.plate = (carDetails.plate || "").trim() || null;
      // .select() so we can detect RLS-denied silent no-ops (0 rows).
      const { data, error } = await supabase
        .from("cars")
        .update(editableFields)
        .eq("id", editingId)
        .select("id");
      if (error) {
        alert("Error: " + error.message);
        setIsLoading(false);
        return;
      }
      if (!data || data.length === 0) {
        alert("No tienes permiso para editar autos (cars.edit).");
        setIsLoading(false);
        return;
      }
      const newPlate = (carDetails.plate || "").trim();
      if (carDetails.alegra_item_id && newPlate && newPlate !== (originalPlate || "").trim()) {
        try {
          await renameAlegraItemPlate(carDetails.alegra_item_id, originalPlate, newPlate);
        } catch (e) {
          alert(
            `El auto se guardó, pero no se pudo actualizar la placa en Alegra (producto ${carDetails.alegra_item_id}): ${e.message}. Corrígelo en Alegra antes de facturar esta unidad.`
          );
        }
      }
      navigate(`/cars/${editingId}`);
    } else {
      const { data, error } = await supabase
        .from("cars")
        .insert({ ...carDetails, plate: (carDetails.plate || "").trim() || null })
        .select("id")
        .single();
      if (error) {
        alert("Error: " + error.message);
        setIsLoading(false);
        return { success: false, error: error.message };
      }
      navigate(`/cars/${data.id}`);
    }
  };

  const handleInvoiceDrop = async (_dropFiles, acceptedFiles) => {
    setInvoiceError(null);
    const xml = acceptedFiles.find((f) =>
      f.name.toLowerCase().endsWith(".xml")
    );
    if (!xml) return;
    setXmlFile(xml);
    setInvoiceLoading(true);

    try {
      const text = await xml.text();
      // Deterministic half: price / folio / date / emisor come straight off
      // the CFDI attributes.
      const cfdi = parseCfdiInvoice(text);
      const facts = extractInvoiceFacts(cfdi);
      // Interpreted half: the vehicle itself, read by the AI edge function.
      const result = await extractVehicleDataAI(text);
      const v = result.vehicle || {};

      const warnings = [...(result.warnings || [])];

      const vin = isPlausibleVin(v.vin) ? String(v.vin).toUpperCase() : "";
      if (v.vin && !vin)
        warnings.push(
          `El VIN leído ("${v.vin}") no tiene el formato esperado de 17 caracteres. Verifícalo.`
        );

      // Duplicate VIN guard — don't prefill if the car already exists.
      if (vin) {
        const { data: existing } = await supabase
          .from("cars")
          .select("id, plate")
          .eq("vin", vin)
          .maybeSingle();
        if (existing) {
          setDuplicateVin(existing);
          setInvoiceInfo(null);
          setInvoiceWarnings([]);
          return;
        }
      }
      setDuplicateVin(null);

      // A low-confidence match is shown as a suggestion, not applied silently.
      const matchedId =
        result.matched_model_id != null &&
        ["high", "medium"].includes(result.model_match_confidence)
          ? result.matched_model_id
          : null;
      if (!matchedId && result.model_match_reason)
        warnings.push(`Modelo: ${result.model_match_reason}`);

      setInvoiceInfo({ ...facts, ...v, vin });
      setInvoiceWarnings(warnings);

      // Merge vehicle fields into carDetails — only fill blanks so a user
      // who hand-typed something first doesn't lose their edit.
      setCarDetails((prev) => {
        const isEmpty = (val) => val === undefined || val === null || val === "";
        const fill = (key, val) =>
          isEmpty(prev[key]) && val ? val : prev[key];
        return {
          ...prev,
          vin: fill("vin", vin),
          year: fill("year", v.year),
          color: fill("color", v.color),
          motor_no: fill("motor_no", v.motor_no),
          acquisition_price: fill(
            "acquisition_price",
            facts.acquisition_price
          ),
          start_date: fill("start_date", facts.start_date),
          invoice_id: fill("invoice_id", facts.invoice_id),
          model: fill("model", matchedId),
        };
      });
    } catch (err) {
      setInvoiceError(
        "No se pudo leer el XML: " +
          (err?.message || err) +
          " — llena los campos manualmente."
      );
      setInvoiceInfo(null);
      setInvoiceWarnings([]);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const removeInvoiceFiles = () => {
    setXmlFile(null);
    setInvoiceInfo(null);
    setInvoiceWarnings([]);
    setInvoiceError(null);
    setDuplicateVin(null);
  };

  const handleInputChange = (value, id) => {
    switch (id) {
      case "plate":
        value = value.toUpperCase();
        value = value.match("[A-Z0-9]*")[0];
        break;
      case "color":
        value = String(value).charAt(0).toUpperCase() + String(value).slice(1);
        break;
      case "year":
        value = value.match("[0-9]{0,4}") ? value.match("[0-9]{0,4}") : "";
        value = parseInt(value);
        break;
      case "acquisition_price":
        break;
      case "equipment_cost":
        break;
      case "vin":
        value = value.toUpperCase();
        value = value.match("[A-Z0-9]*")[0];
        break;
      case "motor_no":
        value = value.toUpperCase();
        value = value.match("[A-Z0-9]*")[0];
        break;
      default:
        break;
    }
    setCarDetails({ ...carDetails, [id]: value });
  };

  useEffect(() => {
    const getModels = async () => {
      const { data, error } = await supabase
        .from("car_models")
        .select(
          `id, name, show_as, nicknames, brand:car_brands(name), car_categories(name)`
        )
        .order("name", { ascending: true });
      if (error) {
        console.error(error.message);
        return { success: false, error: error.message };
      }
      let opts = data
        .map((model) => ({
          value: String(model.id), // 🔴 MUST be string
          label: `${model.brand?.name || ""} ${model.name}`.trim(),
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
        );

      setModels([
        { value: "", label: "Seleccionar" }, // 🔴 empty string, NOT undefined
        ...opts,
      ]);
    };
    const getCarInfo = async () => {
      const { data, error } = await supabase
        .from("cars")
        .select()
        .eq("id", editingId)
        .limit(1);
      if (error) {
        console.error(error.message);
        return { success: false, error: error.message };
      }
      setCarDetails(data[0]);
      setOriginalPlate(data[0]?.plate ?? null);
    };

    getModels();
    if (editingId) {
      getCarInfo();
    }
  }, [editingId]);

  return (
    <AppWrapper>
      <Page
        title={editingId ? "Editar Auto" : "Crear Auto"}
        backAction={{ content: "Autos", url: "/cars" }}
        primaryAction={
          <Button
            variant="primary"
            onClick={saveCarDetails}
            loading={isLoading}
          >
            Guardar
          </Button>
        }
      >
        {!editingId && (
          <div style={{ marginBottom: "1rem" }}>
            <BlockStack gap="300">
              <DropZone
                accept="text/xml, application/xml"
                allowMultiple={false}
                onDrop={handleInvoiceDrop}
                disabled={invoiceLoading}
              >
                <DropZone.FileUpload
                  actionTitle="Subir XML de factura"
                  actionHint="Arrastra el XML de la factura del auto para autollenar los datos"
                />
              </DropZone>
              {invoiceLoading && (
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" />
                  <Text variant="bodySm" tone="subdued">
                    Leyendo la factura…
                  </Text>
                </InlineStack>
              )}
              {duplicateVin && (
                <Banner
                  tone="critical"
                  title="VIN duplicado"
                  action={{
                    content: "Ver auto",
                    onAction: () => navigate(`/cars/${duplicateVin.id}`),
                  }}
                >
                  <p>
                    Ya existe un auto con este VIN
                    {duplicateVin.plate ? ` (placa ${duplicateVin.plate})` : ""}.
                  </p>
                </Banner>
              )}
              {invoiceError && (
                <Banner tone="critical">
                  <p>{invoiceError}</p>
                </Banner>
              )}
              {xmlFile && !invoiceLoading && !invoiceError && (
                <Banner tone="success">
                  <p>XML cargado: {xmlFile.name}</p>
                </Banner>
              )}
              {invoiceInfo && !duplicateVin && (
                <Banner tone="info">
                  <p>
                    Datos extraídos de factura{" "}
                    {invoiceInfo.invoice_id || "sin folio"}
                    {invoiceInfo.emisor_name
                      ? ` (${invoiceInfo.emisor_name})`
                      : ""}
                    . Verifica antes de guardar.
                  </p>
                </Banner>
              )}
              {invoiceWarnings.length > 0 && (
                <Banner tone="warning" title="Revisa estos puntos">
                  <BlockStack gap="100">
                    {invoiceWarnings.map((w, i) => (
                      <p key={i}>{w}</p>
                    ))}
                  </BlockStack>
                </Banner>
              )}
              {xmlFile && !invoiceLoading && (
                <InlineStack>
                  <Button
                    variant="plain"
                    tone="critical"
                    onClick={removeInvoiceFiles}
                  >
                    Quitar archivo
                  </Button>
                </InlineStack>
              )}
              {!xmlFile && !duplicateVin && (
                <Text variant="bodySm" tone="subdued">
                  Opcional — o llena los campos manualmente.
                </Text>
              )}
            </BlockStack>
          </div>
        )}
        <FormLayout>
          <FormLayout.Group condensed>
            <Select
              label="Modelo"
              options={models}
              id="model"
              value={carDetails.model != null ? String(carDetails.model) : ""}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
            />
            <TextField
              label="Placa"
              id="plate"
              value={carDetails.plate}
              placeholder="Sin asignar"
              helpText="Opcional. Se asigna cuando la unidad se emplaca."
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
            />
            <TextField
              label="Color"
              id="color"
              value={carDetails.color}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
            />
            <TextField
              label="Año"
              id="year"
              value={carDetails.year ? carDetails.year.toString() : ""}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
            />
            <TextField
              label="Costo"
              id="acquisition_price"
              value={carDetails.acquisition_price}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
              prefix="$"
              placeholder="0"
              type="number"
            />
            <TextField
              label="Costo de Equipo"
              id="equipment_cost"
              value={carDetails.equipment_cost}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
              prefix="$"
              placeholder="0"
              type="number"
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <TextField
              label="VIN"
              id="vin"
              value={carDetails.vin}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
              disabled={editingId}
            />
            <TextField
              label="No. de Motor"
              id="motor_no"
              value={carDetails.motor_no}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
            />
            <TextField
              label="Odometro"
              suffix="kms"
              id="odometer"
              value={carDetails.odometer}
              disabled
            />
            <TextField
              label="Inicio Operaciones"
              type="date"
              id="start_date"
              value={carDetails.start_date}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TextField
              label="Notas"
              id="notes"
              value={carDetails.notes}
              onChange={(value, id) => {
                handleInputChange(value, id);
              }}
              multiline
            />
          </FormLayout.Group>
        </FormLayout>
      </Page>
    </AppWrapper>
  );
}

export default EditCar;
